import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Loader2, AlertTriangle, X, RotateCcw } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { JobsService } from '@/api/generated/services/JobsService'
import type { JobResponse } from '@/api/generated/models/JobResponse'
import { handleApiError } from '@/utils/api-error-handler'
import {
  isJobActive,
  isJobTerminal,
  formatJobStatus,
  JOB_LIST_POLL_INTERVAL_MS,
} from '@/utils/ai-jobs'

interface AiJobStatusBannerProps {
  workspaceId: string
  /** 새 작업 등록 등으로 즉시 목록을 갱신하고 싶을 때 증가시키는 토큰 */
  refreshToken?: number
}

/** 실패로 끝난 작업을 배너에 계속 노출하는 기간 (오래된 실패가 방문 때마다 뜨지 않도록) */
const FAILED_JOB_VISIBLE_MS = 24 * 60 * 60 * 1000

const jobTypeLabel = (jobType: string): string =>
  jobType === 'ai_analysis' ? 'AI 분석' : jobType

const formatCreatedAt = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}

/**
 * AI 분석 작업 상태 배너.
 * - 페이지 진입 시 진행 중 작업을 복원하고, 진행 중인 동안 주기 폴링한다.
 * - worker_alive:false면 워커 미실행 경고를 표시한다 (온프레미스 배포 사고 감지 수단).
 * - 취소/실패 재시도는 요청자 본인 또는 admin/owner에게만 노출한다.
 */
const AiJobStatusBanner: React.FC<AiJobStatusBannerProps> = ({
  workspaceId,
  refreshToken = 0,
}) => {
  const { showSuccess, showWarning, showError, showInfo } = useAlert()
  const orgMember = useAppSelector(state => state.auth.orgMember)

  const [workerAlive, setWorkerAlive] = useState<boolean | null>(null)
  const [jobs, setJobs] = useState<JobResponse[]>([])
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const [actionBusyId, setActionBusyId] = useState<string | null>(null)

  // 직전 폴링에서 진행 중이던 작업 — 종료로 전환되는 순간 토스트를 띄우기 위해 추적
  const prevActiveIdsRef = useRef<Set<string>>(new Set())

  const fetchJobs = useCallback(async () => {
    if (!workspaceId) return

    try {
      const res = await JobsService.appsJobsViewsListJobs({
        workspaceId,
        page: 1,
        perPage: 10,
      })

      setWorkerAlive(res.worker_alive)

      const prevActive = prevActiveIdsRef.current
      res.data.forEach(job => {
        if (prevActive.has(job.id) && isJobTerminal(job.status)) {
          const label = jobTypeLabel(job.job_type)
          if (job.status === 'completed') {
            showSuccess(`${label} 작업이 완료되었습니다. (성공 ${job.succeeded}건)`)
          } else if (job.status === 'completed_with_errors') {
            showWarning(`${label} 작업이 완료되었지만 ${job.failed}건이 실패했습니다.`)
          } else if (job.status === 'failed') {
            showError(`${label} 작업이 실패했습니다.`)
          }
        }
      })
      prevActiveIdsRef.current = new Set(
        res.data.filter(j => isJobActive(j.status)).map(j => j.id)
      )

      setJobs(res.data)
    } catch (err) {
      // 폴링 실패는 조용히 넘기고 다음 주기에 재시도
      console.error('Failed to fetch AI jobs:', err)
    }
  }, [workspaceId, showSuccess, showWarning, showError])

  // 초기 로드 + 새 작업 등록(refreshToken) 시 즉시 갱신
  useEffect(() => {
    fetchJobs()
  }, [fetchJobs, refreshToken])

  // 진행 중 작업이 있는 동안 주기 폴링
  const hasActive = jobs.some(j => isJobActive(j.status))
  useEffect(() => {
    if (!hasActive) return
    const id = setInterval(fetchJobs, JOB_LIST_POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [hasActive, fetchJobs])

  const canManageJob = (job: JobResponse): boolean =>
    job.requester_id === orgMember?.id ||
    orgMember?.role === 'admin' ||
    orgMember?.role === 'owner'

  const handleCancel = async (job: JobResponse) => {
    const confirmed = window.confirm(
      '진행 중인 AI 분석 작업을 취소하시겠습니까?\n대기 항목은 즉시 취소되고, 실행 중인 항목은 현재 처리까지만 완료 후 중단됩니다.'
    )
    if (!confirmed) return

    setActionBusyId(job.id)
    try {
      const res = await JobsService.appsJobsViewsCancelJobEndpoint({ jobId: job.id })
      showInfo(`취소를 요청했습니다. (대기 항목 ${res.canceled_pending}건 즉시 취소)`)
      await fetchJobs()
    } catch (err) {
      console.error('Failed to cancel job:', err)
      handleApiError(err, showError, '작업 취소에 실패했습니다')
    } finally {
      setActionBusyId(null)
    }
  }

  const handleRetryFailed = async (job: JobResponse) => {
    setActionBusyId(job.id)
    try {
      const res = await JobsService.appsJobsViewsRetryFailedEndpoint({ jobId: job.id })
      showSuccess(`실패 항목 ${res.retried}건을 다시 등록했습니다.`)
      setDismissedIds(prev => {
        const next = new Set(prev)
        next.delete(job.id)
        return next
      })
      await fetchJobs()
    } catch (err) {
      console.error('Failed to retry job items:', err)
      handleApiError(err, showError, '실패 항목 재시도에 실패했습니다')
    } finally {
      setActionBusyId(null)
    }
  }

  const handleDismiss = (jobId: string) => {
    setDismissedIds(prev => new Set(prev).add(jobId))
  }

  // 표시 대상: 진행 중 작업 + 최근 24시간 내 실패로 끝난 작업(닫기 전까지)
  const now = Date.now()
  const visibleJobs = jobs.filter(job => {
    if (dismissedIds.has(job.id)) return false
    if (isJobActive(job.status)) return true

    const failedTerminal =
      job.failed > 0 && (job.status === 'completed_with_errors' || job.status === 'failed')
    if (!failedTerminal) return false
    if (!job.finished_at) return true
    return now - new Date(job.finished_at).getTime() < FAILED_JOB_VISIBLE_MS
  })

  if (workerAlive !== false && visibleJobs.length === 0) return null

  const textStyle: React.CSSProperties = {
    fontFamily: designTokens.typography.fontFamily.sans[0],
    fontSize: '14px',
  }

  return (
    <div className="mt-3 space-y-2">
      {/* 워커 미실행 경고 */}
      {workerAlive === false && (
        <div
          className="flex items-center gap-3 rounded p-3"
          style={{
            backgroundColor: designTokens.colors.error.light,
            border: `1px solid ${designTokens.colors.error.DEFAULT}`,
          }}
        >
          <AlertTriangle
            className="w-5 h-5 flex-shrink-0"
            strokeWidth={2}
            style={{ color: designTokens.colors.error.dark }}
          />
          <span
            style={{
              ...textStyle,
              fontWeight: 600,
              color: designTokens.colors.error.dark,
            }}
          >
            AI 분석 워커가 실행 중이 아닙니다. 등록된 작업이 처리되지 않으니 시스템
            관리자에게 문의하세요.
          </span>
        </div>
      )}

      {/* 작업별 상태 배너 */}
      {visibleJobs.map(job => {
        const active = isJobActive(job.status)
        const busy = actionBusyId === job.id
        const manageable = canManageJob(job)

        return (
          <div
            key={job.id}
            className="flex items-center gap-3 rounded p-3"
            style={
              active
                ? {
                    backgroundColor: designTokens.colors.info.light,
                    border: `1px solid ${designTokens.colors.info.DEFAULT}`,
                  }
                : {
                    backgroundColor: designTokens.colors.warning.light,
                    border: `1px solid ${designTokens.colors.warning.DEFAULT}`,
                  }
            }
          >
            {active ? (
              <Loader2
                className="w-5 h-5 animate-spin flex-shrink-0"
                strokeWidth={2}
                style={{ color: designTokens.colors.info.dark }}
              />
            ) : (
              <AlertTriangle
                className="w-5 h-5 flex-shrink-0"
                strokeWidth={2}
                style={{ color: designTokens.colors.warning.dark }}
              />
            )}

            <div className="flex-1 min-w-0">
              <span
                style={{
                  ...textStyle,
                  fontWeight: 600,
                  color: active
                    ? designTokens.colors.info.dark
                    : designTokens.colors.warning.dark,
                }}
              >
                {jobTypeLabel(job.job_type)} {formatJobStatus(job.status)} —{' '}
                {Math.round(job.progress_pct)}% (전체 {job.total_items}건 · 성공{' '}
                {job.succeeded}건 · 실패 {job.failed}건
                {job.skipped > 0 ? ` · 건너뜀 ${job.skipped}건` : ''})
              </span>
              <span
                className="ml-2"
                style={{
                  ...textStyle,
                  fontSize: '12px',
                  color: designTokens.colors.gray[5],
                }}
              >
                {formatCreatedAt(job.created_at)} 등록
              </span>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {active && manageable && (
                <Button
                  size="xsmall"
                  color="white"
                  onClick={() => handleCancel(job)}
                  disabled={busy}
                >
                  {busy ? '처리 중...' : '취소'}
                </Button>
              )}
              {!active && job.failed > 0 && manageable && (
                <Button
                  size="xsmall"
                  color="white"
                  onClick={() => handleRetryFailed(job)}
                  disabled={busy}
                  className="flex items-center gap-1"
                >
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} />
                  {busy ? '처리 중...' : '실패 재시도'}
                </Button>
              )}
              {!active && (
                <button
                  onClick={() => handleDismiss(job.id)}
                  className="p-1 rounded hover:bg-black/5 transition-colors"
                  aria-label="배너 닫기"
                >
                  <X
                    className="w-4 h-4"
                    style={{ color: designTokens.colors.gray[4] }}
                  />
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default AiJobStatusBanner
